import React from "react";
import tw from "tailwind-styled-components";
import { MdClose } from "react-icons/md";
import moment from "moment";
import {
    ModalHeader,
    ScrollableContent,
    ModalContent,
    ModalRow,
    ModalRowLabel,
    ModalRowValue,
    ModalSection,
    ModalSectionTitle,
} from "../Styles/InputStyles";
import ModalProfileHeaderWithoutImage from "../Common/ModalProfileHeaderWithoutImage";
import { capitalizeFirstLetter } from "../../helpers";

// ---------- Helpers ----------
// Decode index-based bitmask -> array of ids that were completed
const decodeChecklist = (bitmask, sourceList) => {
    if (bitmask == null || !sourceList?.length) return [];
    return sourceList
        .filter((_, index) => (bitmask & (1 << index)) !== 0)
        .map((item) => item.id);
};

const CommonAreaViewDetails = ({
    setIsViewDetails,
    selectedData,
    commonAreaList = [],
}) => {
    const onClose = () => setIsViewDetails(false);

    const record = selectedData;
    const isCleaned = !!record?.id;

    // ✅ Always decode against commonAreaList
    const checklistSource = commonAreaList;
    const checklistLabel = "Cleaning Checklist";

    const completedIds = decodeChecklist(
        record?.taskCompleted,
        checklistSource,
    );
    const hasChecklist = completedIds.length > 0;

    const images = record?.taskImages ?? [];
    const remarks = record?.remarks ?? "";

    const formatDateTime = (date) => {
        if (!date) return "-";
        return moment(date).format("DD, MMM YYYY, hh:mm A");
    };

    // ✅ Header: always show property name only
    const headerName = record?.propName
        ? capitalizeFirstLetter(record.propName)
        : "-";

    return (
        <Container>
            {/* Header */}
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">
                    View Details
                </h1>
                <button onClick={onClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <ModalProfileHeaderWithoutImage
                title="Cleaning Details"
                name={headerName}
                subtitle=""
                onClose={onClose}
            />

            <ModalContent>
                <ScrollableContent>
                    <Wrapper>
                        {/* ---------- Cleaning Task ---------- */}
                        {isCleaned && (
                            <>
                                <BtnWrapper>
                                    <ModalSectionTitle>
                                        Cleaning Details
                                    </ModalSectionTitle>
                                </BtnWrapper>
                                <ModalSection>
                                    <ModalRow>
                                        <FlexCol>
                                            <ModalRowLabel>
                                                Done By
                                            </ModalRowLabel>
                                            <ModalRowValue>
                                                {record?.staffName
                                                    ? capitalizeFirstLetter(
                                                        record.staffName,
                                                    )
                                                    : "-"}
                                            </ModalRowValue>
                                        </FlexCol>
                                        <FlexCol>
                                            <ModalRowLabel>
                                                Staff Mobile
                                            </ModalRowLabel>
                                            <ModalRowValue>
                                                {record?.staffMobile || "-"}
                                            </ModalRowValue>
                                        </FlexCol>
                                    </ModalRow>

                                    <ModalRow>
                                        <FlexCol>
                                            <ModalRowLabel>
                                                Recorded By
                                            </ModalRowLabel>
                                            <ModalRowValue>
                                                {record?.recordedByName ||
                                                    "-"}
                                            </ModalRowValue>
                                        </FlexCol>
                                        <FlexCol>
                                            <ModalRowLabel>
                                                Task Date
                                            </ModalRowLabel>
                                            <ModalRowValue>
                                                {formatDateTime(
                                                    record?.taskDate,
                                                )}
                                            </ModalRowValue>
                                        </FlexCol>
                                    </ModalRow>

                                    {remarks && (
                                        <ModalRow className="grid-cols-1">
                                            <FlexCol>
                                                <ModalRowLabel>
                                                    Remarks
                                                </ModalRowLabel>
                                                <ModalRowValue>
                                                    {remarks}
                                                </ModalRowValue>
                                            </FlexCol>
                                        </ModalRow>
                                    )}
                                </ModalSection>

                                {/* Checklist */}
                                {hasChecklist &&
                                    checklistSource?.length > 0 && (
                                        <>
                                            <BtnWrapper>
                                                <ModalSectionTitle>
                                                    {checklistLabel}
                                                </ModalSectionTitle>
                                            </BtnWrapper>
                                            <ModalSection>
                                                <ChecklistWrapper>
                                                    {checklistSource.map(
                                                        (t) => {
                                                            const done =
                                                                completedIds.includes(
                                                                    t.id,
                                                                );
                                                            return (
                                                                <ChecklistChip
                                                                    key={t.id}
                                                                    $done={done}
                                                                >
                                                                    <span>
                                                                        {done
                                                                            ? "✓"
                                                                            : "✕"}
                                                                    </span>
                                                                    <span>
                                                                        {t.name}
                                                                    </span>
                                                                </ChecklistChip>
                                                            );
                                                        },
                                                    )}
                                                </ChecklistWrapper>
                                            </ModalSection>
                                        </>
                                    )}

                                {/* Images */}
                                {images.length > 0 && (
                                    <>
                                        <BtnWrapper>
                                            <ModalSectionTitle>
                                                Images
                                            </ModalSectionTitle>
                                        </BtnWrapper>
                                        <ModalSection>
                                            <ImageGrid>
                                                {images.map((img, idx) => {
                                                    const uri =
                                                        typeof img ===
                                                            "string"
                                                            ? img
                                                            : img?.uri ??
                                                            img?.url ??
                                                            img?.value ??
                                                            "";
                                                    if (!uri) return null;
                                                    return (
                                                        <ImageThumb
                                                            key={`${uri}-${idx}`}
                                                            src={uri}
                                                            alt={`task-${idx + 1}`}
                                                            onClick={() =>
                                                                window.open(
                                                                    uri,
                                                                    "_blank",
                                                                    "noopener,noreferrer",
                                                                )
                                                            }
                                                        />
                                                    );
                                                })}
                                            </ImageGrid>
                                        </ModalSection>
                                    </>
                                )}
                            </>
                        )}

                        {/* ---------- Not cleaned fallback ---------- */}
                        {!isCleaned && (
                            <ModalSection>
                                <p className="text-sm text-gray-500 py-4 text-center">
                                    No cleaning record found.
                                </p>
                            </ModalSection>
                        )}
                    </Wrapper>
                </ScrollableContent>
            </ModalContent>
        </Container>
    );
};

export default CommonAreaViewDetails;

// ---------------- Styled Components ----------------
const Wrapper = tw.div`grid gap-2 my-0`;
const Container = tw.div`bg-white w-full h-full min-h-[100vh] flex flex-col`;
const BtnWrapper = tw.div`w-full flex items-center justify-between`;
const FlexCol = tw.div`flex flex-col gap-0.5 min-w-0`;

const ChecklistWrapper = tw.div`flex flex-wrap gap-3`;
const ChecklistChip = tw.span`
    inline-flex items-center gap-1
    rounded-full px-3 py-2 text-xs
    ${(p) =>
        p.$done
            ? "bg-green-100 text-green-700"
            : "bg-gray-200 text-gray-500"}
`;

const ImageGrid = tw.div`flex flex-wrap gap-2`;
const ImageThumb = tw.img`h-28 w-28 rounded-lg object-cover border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity`;